import api from './api/axiosConfig';

const hotelService = {
  /**
   * Get point categories (Hill Stations, Lakes, Historical, Resorts, Islamic Sites) with listing counts.
   * @returns {Promise<Array<{id: number, name: string, slug: string, icon: string, order: number, listing_count: number}>>}
   */
  getPointCategories: async () => {
    const response = await api.get('/hotels/point-categories/');
    return response.data;
  },

  /**
   * Get all hotels with optional filters.
   * @param {Object} params - Query parameters (page, page_size, user_latitude, user_longitude,
   *   search, point_category, point_category_id, ordering, etc.)
   */
  getHotels: async (params = {}) => {
    const response = await api.get('/hotels/', { params });
    return response.data;
  },

  /**
   * Get a single hotel by slug
   * @param {string} slug - Hotel slug
   * @param {Object} params - Optional query parameters (user_latitude, user_longitude)
   */
  getHotel: async (slug, params = {}) => {
    const response = await api.get(`/hotels/${slug}/`, { params });
    return response.data;
  },

  /**
   * Create a new hotel
   * @param {FormData} formData - Hotel data including images
   */
  createHotel: async (formData) => {
    const response = await api.post('/hotels/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 120000, // 2 minutes for image uploads
    });
    return response.data;
  },

  /**
   * Update a hotel
   * @param {string} slug - Hotel slug
   * @param {Object} data - Updated hotel data
   */
  updateHotel: async (slug, data) => {
    const response = await api.patch(`/hotels/${slug}/`, data);
    return response.data;
  },

  /**
   * Delete a hotel
   * @param {string} slug - Hotel slug
   */
  deleteHotel: async (slug) => {
    const response = await api.delete(`/hotels/${slug}/`);
    return response.data;
  },

  /**
   * Get hotel statistics
   */
  getHotelStats: async () => {
    const response = await api.get('/hotels/stats/');
    return response.data;
  },

  /**
   * Get reviews for a hotel
   * @param {string} slug - Hotel slug
   */
  getHotelReviews: async (slug) => {
    const response = await api.get(`/hotels/${slug}/reviews/`);
    return response.data;
  },

  /**
   * Create a review for a hotel
   * @param {string} slug - Hotel slug
   * @param {Object} reviewData - Review data (rating, title, comment)
   */
  createHotelReview: async (slug, reviewData) => {
    const response = await api.post(`/hotels/${slug}/reviews/`, reviewData);
    return response.data;
  },

  /**
   * Get user's current location
   * @returns {Promise<{latitude: number, longitude: number}>}
   */
  getUserLocation: () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by your browser'));
      } else {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            });
          },
          (error) => {
            reject(error);
          }
        );
      }
    });
  },

  /**
   * Geocode an address to get coordinates
   * @param {string} address - Address to geocode
   * @returns {Promise<{latitude: number, longitude: number, display_name: string}>}
   */
  geocodeAddress: async (address) => {
    const response = await api.get('/hotels/geocode/', {
      params: { address }
    });
    return response.data;
  },

  /**
   * Get user's location from IP address
   * @returns {Promise<{city: string, region: string, country: string, latitude: number, longitude: number}>}
   */
  getUserLocationFromIP: async () => {
    const response = await api.get('/hotels/user-location/');
    return response.data;
  },

  /**
   * Get road distance between two points
   * @param {number} lat1 - Latitude of first point
   * @param {number} lon1 - Longitude of first point
   * @param {number} lat2 - Latitude of second point
   * @param {number} lon2 - Longitude of second point
   * @returns {Promise<{distance: number, method: string}>}
   */
  getRoadDistance: async (lat1, lon1, lat2, lon2) => {
    const response = await api.get('/hotels/road-distance/', {
      params: { lat1, lon1, lat2, lon2 }
    });
    return response.data;
  },

  /**
   * Get location name from coordinates (reverse geocoding)
   * @param {number} lat - Latitude
   * @param {number} lon - Longitude
   * @returns {Promise<{location_name: string, full_address: string}>}
   */
  reverseGeocode: async (lat, lon) => {
    const response = await api.get('/hotels/reverse-geocode/', {
      params: { lat, lon }
    });
    return response.data;
  },
};

export default hotelService;

